import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { axiosInstance } from "../App";
import { ArrowLeft, Loader2, Mail, Save } from "lucide-react";
import { toast } from "sonner";

function SupervisorSMTPSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    mail_server: "",
    mail_port: 587,
    mail_username: "",
    mail_password: "",
    mail_from: "",
    mail_from_name: "",
    mail_starttls: true,
    mail_ssl_tls: false
  });
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    // Check if user is supervisor
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    if (userData.role !== "supervisor") {
      toast.error("Erişim reddedildi");
      navigate("/dashboard");
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get("/supervisor/smtp-settings");
      if (response.data.smtp_settings) {
        setSmtpForm({
          mail_server: response.data.smtp_settings.mail_server || "",
          mail_port: response.data.smtp_settings.mail_port || 587,
          mail_username: response.data.smtp_settings.mail_username || "",
          mail_password: response.data.smtp_settings.mail_password || "",
          mail_from: response.data.smtp_settings.mail_from || "",
          mail_from_name: response.data.smtp_settings.mail_from_name || "",
          mail_starttls: response.data.smtp_settings.mail_starttls !== false,
          mail_ssl_tls: response.data.smtp_settings.mail_ssl_tls === true
        });
      }
    } catch (error) {
      toast.error("Ayarlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.put("/supervisor/smtp-settings", smtpForm);
      toast.success("SMTP ayarları başarıyla kaydedildi");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "SMTP ayarları kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error("Lütfen e-posta adresi girin");
      return;
    }

    if (!smtpForm.mail_server) {
      toast.error("Lütfen önce SMTP ayarlarını doldurun");
      return;
    }

    setSendingTestEmail(true);
    try {
      const response = await axiosInstance.post("/supervisor/smtp/test-email", null, {
        params: { test_email: testEmailAddress }
      });
      toast.success(response.data.message || "Test e-postası başarıyla gönderildi!");
      setTestEmailAddress("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test e-postası gönderilemedi");
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-20 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2937' }}>
                  Süpervizör SMTP Ayarları
                </h1>
                <p className="text-sm text-gray-500 mt-1">Sistem E-posta Sunucu Yapılandırması</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/supervisor")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Panele Dön
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              <div>
                <CardTitle>Sistem E-posta Ayarları</CardTitle>
                <CardDescription className="mt-1">
                  Yeni firma ve admin kullanıcıları için otomatik e-posta gönderimi yapılandırın
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_server">SMTP Sunucusu *</Label>
                  <Input
                    id="mail_server"
                    value={smtpForm.mail_server}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_server: e.target.value })}
                    placeholder="smtp.gmail.com"
                    required
                  />
                  <p className="text-xs text-gray-500">Örnek: smtp.gmail.com, smtp.office365.com</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_port">SMTP Port *</Label>
                  <Input
                    id="mail_port"
                    type="number"
                    value={smtpForm.mail_port}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_port: parseInt(e.target.value) })}
                    placeholder="587"
                    required
                  />
                  <p className="text-xs text-gray-500">587 (STARTTLS) / 465 (SSL)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_username">Kullanıcı Adı (E-posta) *</Label>
                  <Input
                    id="mail_username"
                    type="email"
                    value={smtpForm.mail_username}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_username: e.target.value })}
                    placeholder="noreply@company.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_password">Şifre / App Password *</Label>
                  <Input
                    id="mail_password"
                    type="password"
                    value={smtpForm.mail_password}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                  <p className="text-xs text-gray-500">Gmail için 16 karakterlik uygulama şifresi</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mail_from">Gönderen E-posta</Label>
                  <Input
                    id="mail_from"
                    type="email"
                    value={smtpForm.mail_from}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_from: e.target.value })}
                    placeholder="noreply@milestora.com"
                  />
                  <p className="text-xs text-gray-500">Boş bırakılırsa kullanıcı adı kullanılır</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mail_from_name">Gönderen İsmi</Label>
                  <Input
                    id="mail_from_name"
                    value={smtpForm.mail_from_name}
                    onChange={(e) => setSmtpForm({ ...smtpForm, mail_from_name: e.target.value })}
                    placeholder="Milestora System"
                  />
                  <p className="text-xs text-gray-500">E-postalarda görünecek isim</p>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="mail_starttls">STARTTLS Kullan</Label>
                    <p className="text-xs text-gray-500">Port 587 için önerilir</p>
                  </div>
                  <Switch
                    id="mail_starttls"
                    checked={smtpForm.mail_starttls}
                    onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, mail_starttls: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="mail_ssl_tls">SSL/TLS Kullan</Label>
                    <p className="text-xs text-gray-500">Port 465 için önerilir</p>
                  </div>
                  <Switch
                    id="mail_ssl_tls"
                    checked={smtpForm.mail_ssl_tls}
                    onCheckedChange={(checked) => setSmtpForm({ ...smtpForm, mail_ssl_tls: checked })}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">📧 Gmail Kullanıyorsanız</h4>
                <p className="text-xs text-blue-800">
                  Gmail için 2FA aktif olmalı ve 16 karakterlik uygulama şifresi oluşturmalısınız.
                  Google Hesap → Güvenlik → 2 Adımlı Doğrulama → Uygulama Şifreleri
                </p>
              </div>

              {/* Test Email Section */}
              <div className="border-t pt-6 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Test E-postası Gönder</h4>
                  <p className="text-xs text-gray-500">Ayarları kaydetmeden önce test edebilirsiniz</p>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestEmail}
                    disabled={sendingTestEmail || !smtpForm.mail_server}
                    className="whitespace-nowrap"
                  >
                    {sendingTestEmail ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Gönderiliyor
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Test Gönder
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Kaydediliyor
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Ayarları Kaydet
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default SupervisorSMTPSettings;
